import { Cpu, Loader2, Plus, Trash2, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import {
  emergencyService,
  type MedicalDevice,
  type MedicalDeviceInput,
} from "../../services/emergency.service";

/**
 * Recording pacemakers, pumps and implants.
 *
 * The emergency record has always had a devices section and nothing could fill
 * it - the card linked to a Health Records screen that has no device editor on
 * it. This is that editor, placed where the section it fills is read.
 *
 * MRI safety is a three-state field on purpose: safe, unsafe, and not recorded.
 * A default of "safe" would be a clinical claim nobody made, and it is the one
 * a radiographer would act on.
 */

const EMPTY: MedicalDeviceInput = { deviceType: "" };

export const MedicalDevices: React.FC<{ onChange?: () => void }> = ({
  onChange,
}) => {
  const [devices, setDevices] = useState<MedicalDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<MedicalDeviceInput>(EMPTY);
  const [mriSafe, setMriSafe] = useState<"unknown" | "yes" | "no">("unknown");
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();

  // Suggestions only: the device type is free text, stored as typed, so the
  // suggestions are offered in the user's language.
  const commonTypes = useMemo(
    () => [
      t.emergxDevPacemaker,
      t.emergxDevIcd,
      t.emergxDevInsulinPump,
      t.emergxDevCochlear,
      t.emergxDevStent,
      t.emergxDevJoint,
      t.emergxDevHeartValve,
      t.emergxDevNeurostimulator,
      t.emergxDevPort,
    ],
    [t],
  );

  const load = async () => {
    try {
      setError(null);
      setDevices(await emergencyService.getDevices());
    } catch {
      setError(t.emergxDevLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.deviceType.trim()) return;

    setSaving(true);
    setError(null);
    try {
      await emergencyService.addDevice({
        ...form,
        ...(mriSafe === "unknown" ? {} : { mriSafe: mriSafe === "yes" }),
      });
      setForm(EMPTY);
      setMriSafe("unknown");
      setAdding(false);
      await load();
      onChange?.();
    } catch {
      setError(t.emergxDevSaveFailed);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (device: MedicalDevice) => {
    setError(null);
    try {
      await emergencyService.removeDevice(device.id);
      await load();
      onChange?.();
    } catch {
      setError(t.emergxDevRemoveFailed);
    }
  };

  return (
    <div
      className="border border-gray-200 rounded-lg p-4"
      data-testid="medical-devices"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="flex items-center gap-2 font-medium text-gray-900">
          <Cpu aria-hidden="true" className="h-4 w-4 text-primary-600" />
          {t.emergxDevTitle}
        </h4>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            data-testid="add-device"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {t.emergxDevAdd}
          </button>
        )}
      </div>

      {error && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
          {t.loading}
        </p>
      ) : devices.length === 0 && !adding ? (
        <p className="text-sm text-gray-500 italic">{t.emergxDevEmpty}</p>
      ) : (
        <ul className="space-y-2">
          {devices.map((device) => (
            <li
              key={device.id}
              data-testid="device-row"
              className="flex items-start justify-between gap-3 rounded-md bg-gray-50 px-3 py-2"
            >
              <div className="min-w-0 text-sm">
                <p className="font-medium text-gray-900">
                  {device.deviceType}
                  {device.deviceName ? ` — ${device.deviceName}` : ""}
                </p>
                <p className="text-gray-600">
                  {[
                    device.manufacturer,
                    device.model,
                    device.implantDate
                      ? t.emergxDevImplantedOn.replace(
                          "{date}",
                          fmt.date(device.implantDate.slice(0, 10)),
                        )
                      : null,
                    // Only stated when someone actually recorded it: silence
                    // here means unknown, not safe.
                    device.mriSafe === true
                      ? t.emergxDevMriSafe
                      : device.mriSafe === false
                        ? t.emergxDevMriUnsafe
                        : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {device.notes && (
                  <p className="text-gray-500">{device.notes}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => void remove(device)}
                aria-label={t.emergxDevRemove.replace(
                  "{type}",
                  device.deviceType,
                )}
                className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600"
              >
                <Trash2 aria-hidden="true" className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <form
          onSubmit={submit}
          className="mt-4 space-y-3"
          data-testid="device-form"
        >
          <div>
            <label
              htmlFor="device-type"
              className="block text-sm font-medium text-gray-700"
            >
              {t.emergxDevType} *
            </label>
            <input
              id="device-type"
              list="device-types"
              required
              value={form.deviceType}
              onChange={(e) => setForm({ ...form, deviceType: e.target.value })}
              placeholder={t.emergxDevPacemaker}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            {/* A list, not a dropdown: these cover most cases without stopping
                anyone entering a device that is not on it. */}
            <datalist id="device-types">
              {commonTypes.map((type) => (
                <option key={type} value={type} />
              ))}
            </datalist>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label
                htmlFor="device-manufacturer"
                className="block text-sm font-medium text-gray-700"
              >
                {t.emergxDevManufacturer}
              </label>
              <input
                id="device-manufacturer"
                value={form.manufacturer ?? ""}
                onChange={(e) =>
                  setForm({ ...form, manufacturer: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="device-model"
                className="block text-sm font-medium text-gray-700"
              >
                {t.emergxDevModel}
              </label>
              <input
                id="device-model"
                value={form.model ?? ""}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="device-date"
                className="block text-sm font-medium text-gray-700"
              >
                {t.emergxDevImplantDate}
              </label>
              <input
                id="device-date"
                type="date"
                value={form.implantDate ?? ""}
                onChange={(e) =>
                  setForm({ ...form, implantDate: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="device-mri"
                className="block text-sm font-medium text-gray-700"
              >
                {t.emergxDevMriLabel}
              </label>
              <select
                id="device-mri"
                value={mriSafe}
                onChange={(e) =>
                  setMriSafe(e.target.value as "unknown" | "yes" | "no")
                }
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {/* Default. Guessing here would put a clinical claim on an
                    emergency card that nobody made. */}
                <option value="unknown">{t.emergxDevNotRecorded}</option>
                <option value="yes">{t.emergxYes}</option>
                <option value="no">{t.emergxNo}</option>
              </select>
            </div>
          </div>

          <div>
            <label
              htmlFor="device-notes"
              className="block text-sm font-medium text-gray-700"
            >
              {t.emergxDevNotes}
            </label>
            <input
              id="device-notes"
              value={form.notes ?? ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={t.emergxDevNotesPlaceholder}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving || !form.deviceType.trim()}
              data-testid="save-device"
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saving && (
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
              )}
              {t.emergxDevSave}
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setForm(EMPTY);
                setMriSafe("unknown");
                setError(null);
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-4 py-2 text-sm"
            >
              <X aria-hidden="true" className="h-4 w-4" />
              {t.cancel}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default MedicalDevices;
